import { useMemo, useState } from "react"

import type {
  SupplierOnboardingStep,
  SupplierOnboardingStepId,
} from "~/pages/SupplierPortal/Onboarding/constants/supplierOnboardingSteps"

type UseOnboardingStepperArgs = Readonly<{
  initialStepId: SupplierOnboardingStepId
  steps: readonly SupplierOnboardingStep[]
}>

const findStepIndex = (
  steps: readonly SupplierOnboardingStep[],
  stepId: SupplierOnboardingStepId
): number => {
  const index = steps.findIndex((step) => step.id === stepId)
  return index >= 0 ? index : 0
}

export const useOnboardingStepper = ({
  initialStepId,
  steps,
}: UseOnboardingStepperArgs) => {
  const externalStepIndex = findStepIndex(steps, initialStepId)
  const [overrideStepIndex, setOverrideStepIndex] = useState<number | null>(
    null
  )

  const currentStepIndex = overrideStepIndex ?? externalStepIndex

  const currentStep = useMemo(
    () => steps[currentStepIndex] ?? steps[0],
    [steps, currentStepIndex]
  )

  const isFirstStep = currentStepIndex === 0
  const isLastStep = currentStepIndex === steps.length - 1

  const goToNextStep = (): void => {
    setOverrideStepIndex((previousIndex) => {
      const index = previousIndex ?? externalStepIndex
      return Math.min(index + 1, steps.length - 1)
    })
  }

  const goToPreviousStep = (): void => {
    setOverrideStepIndex((previousIndex) => {
      const index = previousIndex ?? externalStepIndex
      return Math.max(index - 1, 0)
    })
  }

  return {
    currentStep,
    currentStepIndex,
    isFirstStep,
    isLastStep,
    goToNextStep,
    goToPreviousStep,
  }
}
